import React from 'react';
import { Edit2, Trash2, GripVertical } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { Lesson } from '../types';

interface LessonItemProps {
  lesson: Lesson;
  index: number;
  onEdit: (lesson: Lesson) => void;
  onDelete: (lessonId: number | string) => void;
}

const LessonItem: React.FC<LessonItemProps> = ({ lesson, index, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: lesson.id! });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const badgeColor = lesson.contentType === 'quiz'
    ? '#ff6f00'
    : lesson.lessonType === 'ILTS'
      ? '#9c27b0'
      : '#2196f3';

  const badgeLabel = lesson.contentType === 'quiz'
    ? 'Quiz/Assessment'
    : lesson.lessonType;

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        backgroundColor: 'white',
        padding: '15px',
        borderRadius: '6px',
        border: isDragging ? '2px solid #FF8C00' : '1px solid #e0e0e0',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
      }}
      className="lesson-item"
    >
      <div
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: '#999',
          flexShrink: 0,
        }}
        title="Drag to reorder"
      >
        <GripVertical size={20} />
      </div>

      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '15px' }}>
        <span style={{
          backgroundColor: '#FF8C00',
          color: 'white',
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: '600',
          fontSize: '14px',
          flexShrink: 0,
        }}>
          {index + 1}
        </span>

        <div style={{ flex: 1 }}>
          <h4 style={{ margin: '0 0 6px 0', fontSize: '15px', fontWeight: '600', color: '#333' }}>
            {lesson.title}
          </h4>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <span style={{
              backgroundColor: badgeColor,
              color: 'white',
              padding: '3px 10px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: '500',
            }}>
              {badgeLabel}
            </span>

            {lesson.lessonType === 'Content-Based' && lesson.contentType && lesson.contentType !== 'quiz' && (
              <span style={{
                backgroundColor: '#f5f5f5',
                color: '#666',
                padding: '3px 10px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '500',
                textTransform: 'uppercase',
              }}>
                {lesson.contentType}
              </span>
            )}

            {lesson.lessonDuration && (
              <span style={{ color: '#666', fontSize: '13px' }}>
                {lesson.lessonDuration}
              </span>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button
          onClick={() => onEdit(lesson)}
          className="btn-icon"
          title="Edit Lesson"
          style={{
            padding: '8px',
            backgroundColor: '#f5f5f5',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#666',
          }}
        >
          <Edit2 size={16} />
        </button>
        <button
          onClick={() => lesson.id && onDelete(lesson.id)}
          className="btn-icon btn-danger"
          title="Delete Lesson"
          style={{
            padding: '8px',
            backgroundColor: '#ffebee',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            color: '#d32f2f',
          }}
        >
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );
};

export default React.memo(LessonItem);
